import {
  boolean,
  index,
  integer,
  jsonb,
  pgTable,
  primaryKey,
  text,
  timestamp,
} from "drizzle-orm/pg-core";
import { nanoid } from "nanoid";
import { user } from "./auth";
import type { I18nJsonb } from "./i18n";

// ---- Product Options (สำหรับ generate variant) ----
export const productOption = pgTable("product_option", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => nanoid()),
  name: jsonb("name").$type<I18nJsonb>().notNull(),
  slug: text("slug").notNull().unique(),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at", { mode: "string", withTimezone: true })
    .notNull()
    .defaultNow(),
  updatedAt: timestamp("updated_at", { mode: "string", withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const productOptionValue = pgTable(
  "product_option_value",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    optionId: text("option_id")
      .notNull()
      .references(() => productOption.id, { onDelete: "cascade" }),
    value: jsonb("value").$type<I18nJsonb>().notNull(),
    sortOrder: integer("sort_order").notNull().default(0),
    createdAt: timestamp("created_at", { mode: "string", withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { mode: "string", withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => [index("product_option_value_option_id").on(t.optionId)],
);

// ---- Category (hierarchy) ----
export const category = pgTable(
  "category",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    name: jsonb("name").$type<I18nJsonb>().notNull(),
    slug: text("slug").notNull().unique(),
    /** FK → category.id (self); กำหนดใน relation/migration */
    parentId: text("parent_id"),
    sortOrder: integer("sort_order").notNull().default(0),
    createdAt: timestamp("created_at", { mode: "string", withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { mode: "string", withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    index("category_parent_id").on(t.parentId),
    index("category_slug").on(t.slug),
  ],
);

// ---- Product ----
export const product = pgTable(
  "product",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    name: jsonb("name").$type<I18nJsonb>().notNull(),
    slug: text("slug").notNull().unique(),
    description: jsonb("description").$type<I18nJsonb>(),
    status: text("status", { enum: ["draft", "active", "archived"] })
      .notNull()
      .default("draft"),
    kind: text("kind", {
      enum: ["retail", "production", "hybrid", "online-showcase"],
    })
      .notNull()
      .default("retail"),
    isSellable: boolean("is_sellable").notNull().default(true),
    isShowcaseEligible: boolean("is_showcase_eligible").notNull().default(false),
    showcaseServiceSlugs: jsonb("showcase_service_slugs")
      .$type<string[]>()
      .notNull()
      .default([]),
    createdBy: text("created_by").references(() => user.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at", { mode: "string", withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { mode: "string", withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    index("product_slug").on(t.slug),
    index("product_status").on(t.status),
    index("product_kind").on(t.kind),
    index("product_is_sellable").on(t.isSellable),
    index("product_is_showcase_eligible").on(t.isShowcaseEligible),
    index("product_created_at").on(t.createdAt),
  ],
);

export type ProductKind =
  | "retail"
  | "production"
  | "hybrid"
  | "online-showcase";

export const productVariant = pgTable(
  "product_variant",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    productId: text("product_id")
      .notNull()
      .references(() => product.id, { onDelete: "cascade" }),
    sku: text("sku").notNull(),
    /** ราคา (หน่วยเล็กสุด เช่น satang) */
    price: integer("price").notNull(),
    /** ราคาลด (optional) */
    compareAtPrice: integer("compare_at_price"),
    /** ราคาต้นทุนต่อหน่วย (หน่วยเดียวกับ price) */
    cost: integer("cost").notNull().default(0),
    /** น้ำหนักกรัม; null = ใช้ default 250g */
    weightGrams: integer("weight_grams"),
    createdAt: timestamp("created_at", { mode: "string", withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { mode: "string", withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    index("product_variant_product_id").on(t.productId),
    index("product_variant_sku").on(t.sku),
  ],
);

/** ค่าตัวเลือกต่อ variant (แทน JSON option_values) */
export const productVariantOptionValue = pgTable(
  "product_variant_option_value",
  {
    variantId: text("variant_id")
      .notNull()
      .references(() => productVariant.id, { onDelete: "cascade" }),
    optionId: text("option_id")
      .notNull()
      .references(() => productOption.id, { onDelete: "cascade" }),
    valueId: text("value_id")
      .notNull()
      .references(() => productOptionValue.id, { onDelete: "cascade" }),
  },
  (t) => [
    primaryKey({
      columns: [t.variantId, t.optionId],
      name: "product_variant_option_value_pk",
    }),
    index("product_variant_option_value_value_id").on(t.valueId),
  ],
);

/** เก็บ key ของไฟล์ใน MinIO/S3 (เช่น uploads/products/xxx.jpg) แทนการอ้างอิง media_assets */
export const productImage = pgTable(
  "product_image",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    productId: text("product_id")
      .notNull()
      .references(() => product.id, { onDelete: "cascade" }),
    fileKey: text("file_key").notNull(),
    metadata: jsonb("metadata").$type<
      { blurhash: string } & Record<string, any>
    >(),
    sortOrder: integer("sort_order").notNull().default(0),
    createdAt: timestamp("created_at", { mode: "string", withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    index("product_image_product_id").on(t.productId),
    index("product_image_sort").on(t.productId, t.sortOrder),
  ],
);

// ---- Product ใช้ option ชุดไหน ----
export const productOptionLink = pgTable(
  "product_option_link",
  {
    productId: text("product_id")
      .notNull()
      .references(() => product.id, { onDelete: "cascade" }),
    optionId: text("option_id")
      .notNull()
      .references(() => productOption.id, { onDelete: "cascade" }),
  },
  (t) => [
    primaryKey({
      columns: [t.productId, t.optionId],
      name: "product_option_link_pk",
    }),
  ],
);

/** Product เลือกใช้ value ไหนของแต่ละ option (ถ้าไม่ระบุ = ใช้ทุก value ของ option นั้น)
 * ตัวอย่าง: Option Size มี XS,S,M,L → Product 1 ใช้ S,M,L; Product 2 ใช้ M,L */
export const productOptionValueLink = pgTable(
  "product_option_value_link",
  {
    productId: text("product_id")
      .notNull()
      .references(() => product.id, { onDelete: "cascade" }),
    valueId: text("value_id")
      .notNull()
      .references(() => productOptionValue.id, { onDelete: "cascade" }),
  },
  (t) => [
    primaryKey({
      columns: [t.productId, t.valueId],
      name: "product_option_value_link_pk",
    }),
    index("product_option_value_link_product_id").on(t.productId),
    index("product_option_value_link_value_id").on(t.valueId),
  ],
);

// ---- Tags & Collections ----
export const tag = pgTable("tag", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => nanoid()),
  name: jsonb("name").$type<I18nJsonb>().notNull(),
  slug: text("slug").notNull().unique(),
  createdAt: timestamp("created_at", { mode: "string", withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const collection = pgTable("collection", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => nanoid()),
  name: jsonb("name").$type<I18nJsonb>().notNull(),
  slug: text("slug").notNull().unique(),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at", { mode: "string", withTimezone: true })
    .notNull()
    .defaultNow(),
  updatedAt: timestamp("updated_at", { mode: "string", withTimezone: true })
    .notNull()
    .defaultNow(),
});

// ---- Product <> Category / Tag / Collection ----
export const productCategory = pgTable(
  "product_category",
  {
    productId: text("product_id")
      .notNull()
      .references(() => product.id, { onDelete: "cascade" }),
    categoryId: text("category_id")
      .notNull()
      .references(() => category.id, { onDelete: "cascade" }),
  },
  (t) => [
    primaryKey({
      columns: [t.productId, t.categoryId],
      name: "product_category_pk",
    }),
  ],
);

export const productTag = pgTable(
  "product_tag",
  {
    productId: text("product_id")
      .notNull()
      .references(() => product.id, { onDelete: "cascade" }),
    tagId: text("tag_id")
      .notNull()
      .references(() => tag.id, { onDelete: "cascade" }),
  },
  (t) => [
    primaryKey({ columns: [t.productId, t.tagId], name: "product_tag_pk" }),
  ],
);

export const productCollection = pgTable(
  "product_collection",
  {
    productId: text("product_id")
      .notNull()
      .references(() => product.id, { onDelete: "cascade" }),
    collectionId: text("collection_id")
      .notNull()
      .references(() => collection.id, { onDelete: "cascade" }),
  },
  (t) => [
    primaryKey({
      columns: [t.productId, t.collectionId],
      name: "product_collection_pk",
    }),
  ],
);
